// src/pages/AdminAllStudents.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  Card,
  CardActionArea,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import ModifyStudentModal from "../components/ModifyStudentModal";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ImportExportIcon from '@mui/icons-material/ImportExport';
import AdminNavbar, { AdminTopBar } from "../components/AdminNavbar";
import TuneIcon from '@mui/icons-material/Tune';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  arrayRemove,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { signOut } from "firebase/auth";
import { LEVELS, SUBJECTS } from "../constants";
import { useAdminData } from "../context/AdminDataContext";
import NewStudentForm from "../components/NewStudentForm";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useTranslation } from "react-i18next";
import { deleteStudentAccount } from "../services/studentService";

function AdminAllStudents() {
  const { t } = useTranslation();
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const { students, setStudents, teachers, groups, setGroups, loading } = useAdminData();  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modifyStudentModalOpen, setModifyStudentModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState("validated");


  // 1) Security: ensure current user is admin
  useEffect(() => {
    const verifyAdmin = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const adminQuery = query(
          collection(db, "admin"),
          where("uid", "==", user.uid)
        );
        const querySnapshot = await getDocs(adminQuery);

        if (querySnapshot.empty) {
          console.warn("User is not an admin. Signing out...");
          await signOut(auth);
        }
      } catch (err) {
        console.error("Error verifying admin access:", err);
        await signOut(auth);
      }
    };

    verifyAdmin();
  }, []);



  useEffect(() => {
    const base = students.filter(
      (s) => s.adminValidated === (activeTab === "validated")
    );

    if (!searchTerm) {
      setFilteredStudents(base);
      return;
    }

    const q = searchTerm.toLowerCase();
    setFilteredStudents(
      base.filter(
        (s) =>
          s.firstName?.toLowerCase().includes(q) ||
          s.lastName?.toLowerCase().includes(q) ||
          s.email?.toLowerCase().includes(q)
      )
    );
  }, [students, activeTab, searchTerm]);

  // ---------- DASHBOARD ANALYTICS ----------
  const totalStudents = students.length;

  const activeStudents = students.filter(
    (s) => s.adminValidated === true
  ).length;

  const waitingStudents = students.filter(
    (s) => s.adminValidated === false
  ).length;

  const now = new Date();
  const last7Days = new Date();
  last7Days.setDate(now.getDate() - 7);

  const last30Days = new Date();
  last30Days.setDate(now.getDate() - 30);

  const newLast7Days = students.filter(
    (s) => new Date(s.entryDate) >= last7Days
  ).length;

  const newLast30Days = students.filter(
    (s) => new Date(s.entryDate) >= last30Days
  ).length;

  // 3) Delete a student (and remove them from any groups)
  const handleDeleteStudent = async (studentId) => {
    const confirmDelete = window.confirm(
      t("Êtes-vous sûr de vouloir supprimer cet étudiant ?")
    );
    if (!confirmDelete) return;

    try {
      const studentRef = doc(db, "students", studentId);
      const studentSnapshot = await getDoc(studentRef);
      const studentData = studentSnapshot.exists()
        ? studentSnapshot.data()
        : null;

      if (!studentData) {
        alert(t("Impossible de trouver les données de l'étudiant."));
        return;
      }

      const groupUpdates =
        studentData.enrollments?.map((enrollment) =>
          updateDoc(doc(db, "groups", enrollment.groupId), {
            studentIds: arrayRemove(studentId),
          })
        ) || [];

      await Promise.all(groupUpdates);
      await deleteStudentAccount(studentId);

      setStudents((prev) => prev.filter((s) => s.id !== studentId));
      setFilteredStudents((prev) => prev.filter((s) => s.id !== studentId));

      alert(t("Étudiant supprimé avec succès."));
    } catch (error) {
      console.error("Erreur lors de la suppression de l'étudiant :", error);
      alert(t("Une erreur est survenue."));
    }
  };

  // Approve students
  const handleApproveStudent = async (student) => {
    try {
      const ref = doc(db, "students", student.id);
      await updateDoc(ref, { adminValidated: true });

      // update local students list
      setStudents((prev) =>
        prev.map((s) =>
          s.id === student.id ? { ...s, adminValidated: true } : s
        )
      );

      // open edit modal immediately
      const updatedStudent = { ...student, adminValidated: true };
      setSelectedStudent(updatedStudent);
      setModifyStudentModalOpen(true);
    } catch (error) {
      console.error("Error approving student:", error);
      alert(t("حدث خطأ أثناء تأكيد الطالب. يرجى المحاولة مرة أخرى."));
    }
  };

  // 4) Search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Helper: build display name and initials
  const getStudentName = (s) =>
    `${s.firstName || ""} ${s.lastName || ""}`.trim() || t("طالب بدون اسم");

  const getInitials = (s) => {
    const fn = s.firstName?.[0] || "";
    const ln = s.lastName?.[0] || "";
    return (fn + ln || t("ط")).toUpperCase();
  };

  const handleOpenEditStudent = (student) => {
  setSelectedStudent(student);
  setModifyStudentModalOpen(true);
  };

  const handleCloseEditStudent = () => {
    setSelectedStudent(null);
    setModifyStudentModalOpen(false);
  };

  return (
    <>
      {/* Top bar (logo left, bell right) */}
      <AdminTopBar />

      {/* Layout below the fixed AppBar */}
      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          mt: "64px",
          backgroundColor: "#ffffffff", 
          flexDirection: "row-reverse",
          overflow: "hidden",
        }}
      >
        {/* RIGHT: Sidebar */}
        <AdminNavbar />

        {/* LEFT: Main content (scrollable) */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1, md: 2 },
            overflowY: "auto",
          }}
        >
          {/* Header card (Employee -> Students) */}
          <Card
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: 4,
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.06)", 
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            {/* LEFT SIDE — BUTTONS */}
            <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" size="small">
                   <ImportExportIcon /> {t("Export")}
                </Button>
                <Button
                variant="contained"
                size="small"
                sx={{ bgcolor: "success" }}
                onClick={() => setStudentModalOpen(true)}
                startIcon={<PersonAddAlt1Icon />}
                >
                {t("إضافة طالب")}
                </Button>
            </Box>

            {/* RIGHT SIDE — TITLE + SUBTITLE + ICON */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ textAlign: "right" }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {t("الطلاب")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t("إدارة جميع الطلاب المسجلين في المنصة")}
                </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: "#eef2ff",
                    color: "#4f46e5",
                    width: 46,
                    height: 46,
                    boxShadow: "0 8px 18px rgba(79,70,229,0.18)",
                  }}
                >
                <PeopleAltIcon />
                </Avatar>
            </Box>
          </Card>


          {/* ===== STUDENTS DASHBOARD ANALYTICS ===== */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Total Students */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {totalStudents}
                </Typography>
                <Typography color="text.secondary">{t("Total Students")}</Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: "#e0f2fe",
                  color: "#0369a1",
                  width: 56,
                  height: 56,
                }}
              >
                <SchoolIcon sx={{ fontSize: 32 }} />
              </Avatar>

            </Card>

            {/* Active Students */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {activeStudents}
                </Typography>
                <Typography color="text.secondary">{t("Active Students")}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a", width: 56, height: 56, }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </Card>

            {/* Waiting List */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {waitingStudents}
                </Typography>
                <Typography color="text.secondary">{t("Waiting List")}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: "#fff7ed", color: "#ea580c", width: 56, height: 56 }}>
                <PendingActionsIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </Card>

            {/* New Students */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {newLast7Days}
                </Typography>
                <Typography color="text.secondary">
                  {t("New (7d) · {{count}} (30d)", { count: newLast30Days })}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: "#eef2ff", color: "#4f46e5", width: 56, height: 56  }}>
                <TrendingUpIcon sx={{ fontSize: 32 }}/>
              </Avatar>
            </Card>
          </Box>


          {/* Tabs row + Search/Filter (same line) */}
          <Box
            sx={{
              direction: "rtl",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2.5,
            }}
          >
            {/* RIGHT: Tabs */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 0.75,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                border: "1px solid rgba(0,0,0,0.06)",
                width: "fit-content",
              }}
            >
              <Button
                disableRipple
                onClick={() => setActiveTab("validated")}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 700,
                  textTransform: "none",
                  backgroundColor: activeTab === "validated" ? "#eef2ff" : "transparent",
                  color: activeTab === "validated" ? "#4f46e5" : "text.secondary",
                }}
              >
                {t("الطلاب المسجلين بالكامل")}
              </Button>

              <Button
                disableRipple
                onClick={() => setActiveTab("unvalidated")}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 700,
                  textTransform: "none",
                  backgroundColor: activeTab === "unvalidated" ? "#fff7ed" : "transparent",
                  color: activeTab === "unvalidated" ? "#ea580c" : "text.secondary",
                }}
              >
                {t("الطلاب غير المسجلين بالكامل")}
              </Button>
            </Box>

            {/* LEFT: Filter + Search */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder={t("ابحث عن طالب بالاسم أو البريد...")}
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{
                  width: 320,
                  backgroundColor: "#f9fafb",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 2,
                  borderColor: "rgba(0,0,0,0.12)",
                  backgroundColor: "#fff",
                  minWidth: 42,
                  "&:hover": { backgroundColor: "#f9fafb", borderColor: "rgba(0,0,0,0.18)" },
                }}
              >
                <TuneIcon sx={{ color: "rgba(0,0,0,0.6)" }} />
              </Button>
            </Box>
          </Box>

          {/* Students table (styled like the Request Time Off table) */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "#ffffff",
              boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Table sx={{ direction: "rtl" }}>
            <TableHead sx={{ bgcolor: "#ffffffff" }}>
                <TableRow>
                <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("اسم الطالب")}</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("المستوى")}</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("القسم")}</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("البريد الإلكتروني")}</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("الهاتف")}</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("الحالة")}</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("الإجراءات")}</TableCell>
                </TableRow>
            </TableHead>

            <TableBody>
                {loading ? (
                <TableRow>
                    <TableCell colSpan={7} align="center">
                    {t("جاري تحميل قائمة الطلاب...")}
                    </TableCell>
                </TableRow>
                ) : filteredStudents.length === 0 ? (
                <TableRow>
                    <TableCell colSpan={7} align="center">
                    {t("لا يوجد طلاب مطابقون لعملية البحث.")}
                    </TableCell>
                </TableRow>
                ) : (
                filteredStudents.map((student) => (
                  <TableRow
                    key={student.id}
                    hover
                    sx={{
                      direction: "rtl",
                      "& td": { py: 2 }, 
                      "&:hover": { backgroundColor: "#fafafa" },
                    }}
                    >
                  
                    {/* 1 — NAME (RIGHTMOST) */}
                    <TableCell sx={{ textAlign: "right" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexDirection: "row-reverse" }}>
                        <Box>
                            <Typography sx={{ fontWeight: 500 }}>
                            {getStudentName(student)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                            {student.studentCode || ""}
                            </Typography>
                        </Box>
                        </Box>
                    </TableCell>

                    {/* 2 — LEVEL */}
                    <TableCell sx={{ textAlign: "right" }}>
                        <Chip
                        label={student.level || t("غير محدد")}
                        size="small"
                        sx={{ bgcolor: "#e0f2fe", color: "#0369a1", borderRadius: "999px" }}
                        />
                    </TableCell>

                    {/* 3 — GROUP */}
                    <TableCell sx={{ textAlign: "right" }}>
                        {student.enrollments?.length > 0
                        ? student.enrollments[0].groupName || student.enrollments[0].groupId
                        : t("-")}
                    </TableCell>

                    {/* 4 — EMAIL */}
                    <TableCell sx={{ textAlign: "right" }}>
                        {student.email || t("-")}
                    </TableCell>

                    {/* 5 — PHONE */}
                    <TableCell sx={{ textAlign: "right" }}>
                        {student.phone || t("-")}
                    </TableCell>

                    {/* 6 — STATUS */}
                    <TableCell sx={{ textAlign: "right" }}>
                      <Chip
                        label={student.adminValidated ? t("ACTIVE") : t("WAITING")}
                        size="small"
                        sx={{
                          bgcolor: student.adminValidated
                            ? "rgba(34,197,94,0.12)"
                            : "rgba(251,191,36,0.18)",
                          color: student.adminValidated
                            ? "rgba(22,163,74,0.95)"
                            : "#b45309",
                          borderRadius: "5px",
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>

                    {/* 7 — ACTIONS (LEFTMOST) */}
                    <TableCell sx={{ textAlign: "right" }}>
                      <Box sx={{ display: "flex", flexDirection: "row-reverse", gap: 0.5 }}>

                        {/* DELETE: for everyone */}
                        <Button
                          variant="text"
                          size="small"
                          color="error"
                          onClick={() => handleDeleteStudent(student.id)}
                          sx={{ fontWeight: 700, minWidth: 0, px: 1 }}
                          title={t("حذف")}
                        >
                          <DeleteOutlinedIcon />
                        </Button>

                        {/* APPROVED STUDENTS: show EDIT */}
                        {student.adminValidated === true && (
                          <Button
                            size="small"
                            onClick={() => handleOpenEditStudent(student)}
                            sx={{
                              fontWeight: 800,
                              minWidth: 0,
                              px: 1,
                              "&:hover": { backgroundColor: "#f9fafb" },
                            }}
                            title={t("تعديل الطالب")}
                          >
                            <BorderColorOutlinedIcon sx={{ color: "#50667dff" }} />
                          </Button>
                        )}

                        {/* WAITING LIST: show APPROVE */}
                        {student.adminValidated === false && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleApproveStudent(student)}
                            sx={{
                              fontWeight: 800,
                              color: "rgba(22,163,74,0.95)",
                              "&:hover": { backgroundColor: "rgba(34,197,94,0.10)" },
                              minWidth: 0,
                              px: 1,
                            }}
                            title={t("تأكيد الطالب")}
                          >
                            <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
                )}
            </TableBody>
            </Table>
          </Paper>

          {/* Add student modal */}
          <Dialog
            open={studentModalOpen}
            onClose={() => setStudentModalOpen(false)}
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
              },
            }}
          >
            <DialogContent
              sx={{
                p: { xs: 2, md: 3 },
                bgcolor: "background.default",
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                "& .MuiInputBase-root": { borderRadius: 2 },
                "& .MuiButton-containedPrimary": {
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                },
                "& .MuiButton-outlinedPrimary": { borderRadius: 2 },
              }}
            >
              <NewStudentForm
                open={studentModalOpen}
                onClose={() => setStudentModalOpen(false)}
                onSubmit={(newStudent) => {
                  setStudents((prev) => [...prev, newStudent]);
                  setFilteredStudents((prev) => [...prev, newStudent]);
                }}
                students={students}
                levels={LEVELS || []}
                subjects={SUBJECTS || []}
                teachers={teachers || []}
                groups={groups || []}
                setGroups={setGroups}
              />
            </DialogContent>
          </Dialog>

          {selectedStudent && (
            <ModifyStudentModal
              open={modifyStudentModalOpen}
              onClose={() => setModifyStudentModalOpen(false)}
              student={selectedStudent}
              setStudents={setStudents}
              subjects={SUBJECTS}
              teachers={teachers || []} 
              groups={groups}
            />
          )}
        </Box>
      </Box>
    </>
  );
}

export default AdminAllStudents;
